import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { Bar } from "react-chartjs-2";
import dayjs from "dayjs";
import {
    Truck,
    AlertTriangle,
    PackageCheck,
    MapPin,
    FileCheck2,
    FileWarning,
    ClipboardX,
    Flame,
    TrendingDown,
    CheckCircle2,
    CalendarX,
} from "lucide-react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import { useNavigate } from "react-router-dom";
import {
    UI,
    toneColor,
    priorityTone,
    safeNumber,
    formatMoney,
    shortLabel,
    fetchExchangeRates,
    convertCurrency,
    KpiCard,
    KpiGrid,
    Panel,
    ActionList,
    RankList,
    DonutWithLegend,
    MetricRow,
    SegmentedTabs,
    DashboardToolbar,
    barChartOptions,
    ProgressStat,
    TimelineList,
    relativeDays,
    TruncatedText,
    StackedCell,
    DetailsButton,
    TableCard,
    withRawSort,
    dateValue,
    useDateRange,
    downloadCsv,
    csvDate,
    csvMoney,
    PRIORITY_RANK,
} from "../OceanTMS/oceanDashboardUi";
import FileStatusDrawer from "../OceanTMS/FileStatusDrawer";

const safeText = (value, fallback = "—") => {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (Array.isArray(value)) return value.map((item) => safeText(item, "")).filter(Boolean).join(", ");
    if (typeof value === "object") return value.companyName || value.name || value.label || value.title || value.value || fallback;
    return fallback;
};

const STAGE_TONES = {
    "Pickup Planning": "danger",
    "Waiting Departure": "warning",
    "In Transit": "info",
    "Delivered / POD Pending": "teal",
    "Ready to Close": "success",
    Open: "neutral",
};

const STAGE_ORDER = Object.keys(STAGE_TONES);

const BREAKDOWNS = [
    { value: "customer", label: "Customers" },
    { value: "shipper", label: "Shippers" },
    { value: "consignee", label: "Consignees" },
    { value: "serviceProvider", label: "Service Providers" },
    { value: "roadTransportType", label: "Road Mode" },
    { value: "truckType", label: "Truck Type" },
    { value: "pickupCountry", label: "Pickup Country" },
    { value: "deliveryCountry", label: "Delivery Country" },
    { value: "priority", label: "Priority" },
    { value: "docHealth", label: "Doc Health" },
];

const place = (city, country) => [city, country].filter((v) => v && v !== "—").join(", ") || "—";

function RhflDashboard() {
    const navigate = useNavigate();
    const clickEditHandler = (id) => {
        if (!id) return;
        navigate(`/shipping-order/create-shipment/details?id=${id}`);
    };

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [currency, setCurrency] = useState("EUR");
    const [exchangeRates, setExchangeRates] = useState(undefined);
    const { dateRange, setDateRange, inRange } = useDateRange();
    const [tableFilters, setTableFilters] = useState([]);
    const [breakdown, setBreakdown] = useState("customer");
    const [selectedId, setSelectedId] = useState(null);
    const tableRef = React.useRef(null);

    const filterTitleMap = {
        customer: "Customer",
        shipper: "Shipper",
        consignee: "Consignee",
        roadTransportType: "Road Mode",
        truckType: "Truck Type",
        serviceProvider: "Service Provider",
        pickupCountry: "Pickup Country",
        deliveryCountry: "Delivery Country",
        docHealth: "Document Health",
        stage: "Current Stage",
        priority: "Priority",
        actionRequired: "Action Required",
        missingDocuments: "Missing Documents",
        pickupSoon: "Pickup Soon",
        deliverySoon: "Delivery Soon",
        podPending: "POD Pending",
        adrFiles: "ADR Files",
        negativeMargin: "Negative Margin",
        readyToClose: "Ready to Close",
    };

    const setFilter = (type, value, label) => {
        const title = filterTitleMap[type] || "Filter";
        const newFilter = { type, value: value ?? null, label: value ? `${title}: ${label || value}` : title };
        setTableFilters((prev) => {
            const exists = prev.some((f) => f.type === newFilter.type && f.value === newFilter.value);
            if (exists) return prev.filter((f) => !(f.type === newFilter.type && f.value === newFilter.value));
            return [...prev, newFilter];
        });
        setTimeout(() => tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    };

    const isFilterActive = (type, value = null) =>
        tableFilters.some((f) => f.type === type && (value === null || f.value === value));
    const activeFilterValue = (type) => tableFilters.find((f) => f.type === type)?.value;
    const removeFilter = (filter) =>
        setTableFilters((prev) => prev.filter((f) => !(f.type === filter.type && f.value === filter.value)));
    const clearFilter = () => setTableFilters([]);

    useEffect(() => {
        fetchExchangeRates().then(setExchangeRates);
    }, []);

    useEffect(() => {
        const fetchShipments = async () => {
            try {
                const res = await CrudService.getShipments();
                const raw = res?.data?.data || res?.data || [];
                const normalized = raw.map((item) => {
                    const a = item.attributes || item;
                    const cargoDetails = Array.isArray(a.cargoDetails) ? a.cargoDetails : [];
                    const totals = a.totals || {};
                    const buying =
                        safeNumber(a.buyingRate) +
                        safeNumber(a.pickupRate) +
                        safeNumber(a.deliveryRate) +
                        safeNumber(a.exportCustomsRate) +
                        safeNumber(a.importCustomsRate);
                    const selling = safeNumber(a.sellingRate || a.sallingRate);
                    const profit = safeNumber(a.profit || selling - buying);
                    return {
                        id: item.id || item._id,
                        rhflNo: a.shipmentNo ? `RHFL${a.shipmentNo}` : "—",
                        customer: safeText(a.customer),
                        shipper: safeText(a.shipper),
                        consignee: safeText(a.consignee),
                        roadTransportType: a.roadTransportType || "Unknown",
                        truckType: a.truckType || "Unknown",
                        // Multi-truck files list their trucks; older files only have the file-level plate.
                        truckCount: Array.isArray(a.trucks) && a.trucks.length ? a.trucks.length : 1,
                        plates:
                            (Array.isArray(a.trucks) && a.trucks.length
                                ? a.trucks.map((t) => [t.plate, t.trailerPlate].filter(Boolean).join(" / ")).filter(Boolean).join(", ")
                                : a.plateContainerNumber) || "—",
                        serviceProvider: safeText(a.serviceProvider),
                        pickupCountry: a.pickupLocation?.country || "—",
                        pickupCity: a.pickupLocation?.city || "—",
                        deliveryCountry: a.deliveryLocation?.country || "—",
                        deliveryCity: a.deliveryLocation?.city || "—",
                        pickupDate: a.pickupDate,
                        estimatedDeparture: a.estimatedDeparture,
                        actualDeparture: a.actualDeparture,
                        estimatedDeliveryDate: a.estimatedDeliveryDate,
                        actualDelivery: a.actualDelivery,
                        proofOfDelivery: a.proofOfDelivery,
                        exportDeclaration: a.exportDeclaration,
                        importDeclaration: a.importDeclaration,
                        atr: a.atr,
                        cmr: a.cmr,
                        t1t2: a.t1t2,
                        packingList: a.packingList,
                        commercialInvoice: a.commercialInvoice,
                        signedCmr: a.signedCmr,
                        closedT1t2: a.closedT1t2,
                        adr: Boolean(a.adr),
                        imco: a.imco || "",
                        buying,
                        selling,
                        profit,
                        currency: a.sellingRateCurrency || a.currency || "EUR",
                        cargoCount: cargoDetails.length,
                        grossWeight:
                            safeNumber(totals.totalGrossWeight) ||
                            cargoDetails.reduce((sum, c) => sum + safeNumber(c.grossWeight), 0),
                        ldm: safeNumber(totals.totalLdm),
                        cbm: safeNumber(totals.totalCbm),
                        chargeableWeight: safeNumber(totals.totalChargeableWeight),
                    };
                });
                setRows(normalized);
            } catch (error) {
                console.error("RHFL dashboard fetch error:", error);
                setRows([]);
            } finally {
                setLoading(false);
            }
        };
        fetchShipments();
    }, []);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const dated = rows.filter((row) => inRange(row.pickupDate));
        if (!q) return dated;
        return dated.filter((row) =>
            [
                row.rhflNo,
                row.customer,
                row.shipper,
                row.consignee,
                row.roadTransportType,
                row.truckType,
                row.plates,
                row.serviceProvider,
                row.pickupCountry,
                row.deliveryCountry,
                row.pickupCity,
                row.deliveryCity,
            ]
                .join(" ")
                .toLowerCase()
                .includes(q)
        );
    }, [rows, search, dateRange]);

    // Dosya alanları dizi olarak saklanabilir (ör. []); boş dizi de "eksik" sayılmalı
    const isDocMissing = (value) => (Array.isArray(value) ? value.length === 0 : !value);
    const hasDoc = (value) => !isDocMissing(value);

    const DOCS = [
        ["Export Declaration", "exportDeclaration"],
        ["ATR", "atr"],
        ["Commercial Invoice", "commercialInvoice"],
        ["Packing List", "packingList"],
        ["T1/T2", "t1t2"],
        ["CMR", "cmr"],
    ];

    const getMissingDocs = (row) => DOCS.filter(([, key]) => isDocMissing(row[key])).map(([name]) => name);

    const getDocumentHealth = (row) =>
        Math.round((DOCS.filter(([, key]) => hasDoc(row[key])).length / DOCS.length) * 100);

    const getDocumentHealthBucket = (row) => {
        const health = getDocumentHealth(row);
        if (health <= 40) return "0-40% Critical";
        if (health <= 70) return "41-70% Needs Action";
        if (health < 100) return "71-99% Almost Complete";
        return "100% Complete";
    };

    const getCurrentStage = (row) => {
        if (!row.pickupDate) return "Pickup Planning";
        if (!row.actualDeparture) return "Waiting Departure";
        if (row.actualDeparture && !row.actualDelivery) return "In Transit";
        if (row.actualDelivery && !row.proofOfDelivery) return "Delivered / POD Pending";
        if (row.proofOfDelivery) return "Ready to Close";
        return "Open";
    };

    const getConvertedSelling = (row) => convertCurrency(row.selling, row.currency, currency, exchangeRates);
    const getConvertedBuying = (row) => convertCurrency(row.buying, row.currency, currency, exchangeRates);
    const getConvertedProfit = (row) => getConvertedSelling(row) - getConvertedBuying(row);

    const within = (date, days) =>
        date && dayjs(date).diff(dayjs(), "day") >= 0 && dayjs(date).diff(dayjs(), "day") <= days;
    const isPickupSoon = (row) => within(row.pickupDate, 2);
    const isDeliverySoon = (row) => within(row.estimatedDeliveryDate, 3);

    const getPriority = (row) => {
        const missingDocs = getMissingDocs(row);
        if (getConvertedProfit(row) < 0) return "Critical";
        if (row.adr || isPickupSoon(row)) return "High";
        if (isDeliverySoon(row) || missingDocs.length >= 2) return "High";
        if (missingDocs.length === 1) return "Medium";
        return "Normal";
    };

    const applyTableFilter = (data) => {
        if (!tableFilters.length) return data;
        return data.filter((row) =>
            tableFilters.every((filter) => {
                switch (filter.type) {
                    case "customer":
                    case "shipper":
                    case "consignee":
                    case "roadTransportType":
                    case "truckType":
                    case "serviceProvider":
                    case "pickupCountry":
                    case "deliveryCountry":
                        return row[filter.type] === filter.value;
                    case "docHealth":
                        return getDocumentHealthBucket(row) === filter.value;
                    case "stage":
                        return getCurrentStage(row) === filter.value;
                    case "priority":
                        return getPriority(row) === filter.value;
                    case "actionRequired":
                        return getPriority(row) !== "Normal";
                    case "missingDocuments":
                        return getMissingDocs(row).length > 0;
                    case "pickupSoon":
                        return isPickupSoon(row);
                    case "deliverySoon":
                        return isDeliverySoon(row);
                    case "podPending":
                        return row.actualDelivery && !row.proofOfDelivery;
                    case "adrFiles":
                        return row.adr;
                    case "negativeMargin":
                        return getConvertedProfit(row) < 0;
                    case "readyToClose":
                        return getCurrentStage(row) === "Ready to Close";
                    default:
                        return true;
                }
            })
        );
    };

    const tableRows = useMemo(
        () => applyTableFilter(filteredRows),
        [filteredRows, tableFilters, currency, exchangeRates]
    );

    const kpis = useMemo(() => {
        const total = tableRows.length;
        const totalSelling = tableRows.reduce((s, r) => s + getConvertedSelling(r), 0);
        const totalBuying = tableRows.reduce((s, r) => s + getConvertedBuying(r), 0);
        const totalProfit = totalSelling - totalBuying;
        return {
            total,
            totalSelling,
            totalBuying,
            totalProfit,
            margin: totalSelling ? (totalProfit / totalSelling) * 100 : 0,
            actionRequired: tableRows.filter((r) => getPriority(r) !== "Normal").length,
            missingDocuments: tableRows.filter((r) => getMissingDocs(r).length > 0).length,
            avgDocumentHealth: total
                ? Math.round(tableRows.reduce((sum, row) => sum + getDocumentHealth(row), 0) / total)
                : 0,
            pickupSoon: tableRows.filter(isPickupSoon).length,
            deliverySoon: tableRows.filter(isDeliverySoon).length,
            pickupPlanning: tableRows.filter((r) => !r.pickupDate).length,
            podPending: tableRows.filter((r) => r.actualDelivery && !r.proofOfDelivery).length,
            readyToClose: tableRows.filter((r) => getCurrentStage(r) === "Ready to Close").length,
            adrFiles: tableRows.filter((r) => r.adr).length,
            negativeMargin: tableRows.filter((r) => getConvertedProfit(r) < 0).length,
        };
    }, [tableRows, currency, exchangeRates]);

    const groupBy = (getter, { limit = 8, sort = true } = {}) => {
        const map = {};
        filteredRows.forEach((row) => {
            const label = safeText(getter(row), "Unknown");
            map[label] = (map[label] || 0) + 1;
        });
        const list = Object.entries(map).map(([label, count]) => ({ label, count }));
        return sort ? list.sort((a, b) => b.count - a.count).slice(0, limit) : list;
    };

    const stageRows = useMemo(
        () =>
            groupBy(getCurrentStage, { sort: false })
                .sort((a, b) => STAGE_ORDER.indexOf(a.label) - STAGE_ORDER.indexOf(b.label))
                .map((d) => ({ ...d, color: toneColor(STAGE_TONES[d.label]) })),
        [filteredRows]
    );

    const breakdownRows = useMemo(() => {
        switch (breakdown) {
            case "priority":
                return ["Critical", "High", "Medium", "Normal"]
                    .map((label) => ({
                        label,
                        count: filteredRows.filter((r) => getPriority(r) === label).length,
                        color: toneColor(priorityTone(label)),
                    }))
                    .filter((d) => d.count > 0);
            case "docHealth":
                return [
                    ["0-40% Critical", "danger"],
                    ["41-70% Needs Action", "warning"],
                    ["71-99% Almost Complete", "info"],
                    ["100% Complete", "success"],
                ].map(([label, tone]) => ({
                    label,
                    count: filteredRows.filter((r) => getDocumentHealthBucket(r) === label).length,
                    color: toneColor(tone),
                }));
            default:
                return groupBy((row) => row[breakdown]);
        }
    }, [filteredRows, breakdown, currency, exchangeRates]);

    const monthlyRows = useMemo(
        () =>
            groupBy((row) => (row.pickupDate ? dayjs(row.pickupDate).format("YYYY-MM") : null), { sort: false })
                .filter((d) => d.label !== "Unknown")
                .sort((a, b) => a.label.localeCompare(b.label))
                .slice(-12),
        [filteredRows]
    );

    const actionItems = [
        { key: "stage", value: "Pickup Planning", label: "Pickup not planned", description: "No pickup date set", icon: CalendarX, tone: "danger", count: kpis.pickupPlanning },
        { key: "missingDocuments", label: "Missing documents", description: "At least one required document missing", icon: FileWarning, tone: "warning", count: kpis.missingDocuments },
        { key: "podPending", label: "POD pending", description: "Delivered but POD not uploaded", icon: ClipboardX, tone: "warning", count: kpis.podPending },
        { key: "adrFiles", label: "ADR / dangerous goods", description: "Requires ADR handling", icon: Flame, tone: "danger", count: kpis.adrFiles },
        { key: "negativeMargin", value: true, label: "Negative margin", description: "Buying exceeds selling", icon: TrendingDown, tone: "danger", count: kpis.negativeMargin },
        { key: "readyToClose", label: "Ready to close", description: "POD uploaded, file can be closed", icon: CheckCircle2, tone: "success", count: kpis.readyToClose },
    ].map((item) => ({
        ...item,
        active: isFilterActive(item.key, item.value ?? null),
        onClick: () => setFilter(item.key, item.value ?? null),
    }));

    // Önümüzdeki 14 gün içindeki (veya gecikmiş) yükleme / teslim hareketleri
    const upcomingMovements = useMemo(() => {
        const events = [];
        const inWindow = (date, from) => {
            const d = dayjs(date).startOf("day").diff(dayjs().startOf("day"), "day");
            return d >= from && d <= 14;
        };

        tableRows.forEach((row) => {
            if (row.pickupDate && !row.actualDeparture && inWindow(row.pickupDate, -3)) {
                events.push({ row, kind: "pickup", date: row.pickupDate });
            }
            if (row.estimatedDeliveryDate && !row.actualDelivery && inWindow(row.estimatedDeliveryDate, -3)) {
                events.push({ row, kind: "delivery", date: row.estimatedDeliveryDate });
            }
        });

        return events
            .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
            .slice(0, 6)
            .map(({ row, kind, date }) => {
                const overdue = dayjs(date).isBefore(dayjs(), "day");
                return {
                    key: `${row.id}-${kind}`,
                    icon: kind === "pickup" ? Truck : MapPin,
                    tone: overdue ? "danger" : kind === "pickup" ? "teal" : "info",
                    title: `${row.rhflNo} · ${kind === "pickup" ? "Pickup" : "Delivery"} ${kind === "pickup" ? place(row.pickupCity, row.pickupCountry) : place(row.deliveryCity, row.deliveryCountry)}`,
                    description: `${row.customer} · ${row.serviceProvider}`,
                    when: overdue ? `Overdue · ${relativeDays(date)}` : relativeDays(date),
                    whenColor: overdue ? UI.red : UI.text,
                    date: dayjs(date).format("DD MMM"),
                    onClick: () => setSelectedId(row.id),
                };
            });
    }, [tableRows]);

    const monthDelta = useMemo(() => {
        const count = (m) => filteredRows.filter((r) => r.pickupDate && dayjs(r.pickupDate).format("YYYY-MM") === m).length;
        return count(dayjs().format("YYYY-MM")) - count(dayjs().subtract(1, "month").format("YYYY-MM"));
    }, [filteredRows]);

    const health = {
        total: tableRows.length,
        pickupPlanned: tableRows.filter((r) => r.pickupDate).length,
        docsComplete: tableRows.filter((r) => getMissingDocs(r).length === 0).length,
        departed: tableRows.filter((r) => r.actualDeparture).length,
        profitable: tableRows.filter((r) => getConvertedProfit(r) > 0).length,
    };

    const getPriorityReasons = (row) => {
        const reasons = [];
        const missingDocs = getMissingDocs(row);
        if (getConvertedProfit(row) < 0) reasons.push("Negative margin: buying exceeds selling");
        if (row.adr) reasons.push(`ADR / dangerous goods${row.imco ? ` · ${row.imco}` : ""}`);
        if (isPickupSoon(row)) reasons.push(`Pickup ${relativeDays(row.pickupDate).toLowerCase()}`);
        if (isDeliverySoon(row)) reasons.push(`Delivery ${relativeDays(row.estimatedDeliveryDate).toLowerCase()}`);
        if (missingDocs.length) {
            reasons.push(`${missingDocs.length} required document${missingDocs.length > 1 ? "s" : ""} missing`);
        }
        return reasons.length ? reasons : ["No open issues on this file"];
    };

    // Detay panelinde gösterilecek aşama / evrak / öncelik özeti
    const buildStatus = (row) => {
        const stage = getCurrentStage(row);
        const priority = getPriority(row);
        const selling = getConvertedSelling(row);
        const buying = getConvertedBuying(row);
        const fmt = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—");
        const short = (d) => (d ? dayjs(d).format("DD MMM") : null);

        const steps = [
            { label: "Pickup scheduled", done: Boolean(row.pickupDate), date: short(row.pickupDate) },
            { label: "Departed", done: Boolean(row.actualDeparture), date: short(row.actualDeparture) },
            {
                label: "Delivered",
                done: Boolean(row.actualDelivery),
                date: row.actualDelivery
                    ? short(row.actualDelivery)
                    : row.estimatedDeliveryDate
                        ? `ETA ${short(row.estimatedDeliveryDate)}`
                        : null,
            },
            { label: "POD uploaded · ready to close", done: hasDoc(row.proofOfDelivery), date: null },
        ];
        const currentIndex = steps.findIndex((step) => !step.done);

        return {
            title: row.rhflNo,
            subtitle: row.customer,
            priority,
            pills: [
                { label: stage, tone: STAGE_TONES[stage] },
                { label: `${priority} priority`, tone: priorityTone(priority) },
                ...(row.adr ? [{ label: "ADR", tone: "danger" }] : []),
            ],
            facts: [
                ["Origin", place(row.pickupCity, row.pickupCountry)],
                ["Destination", place(row.deliveryCity, row.deliveryCountry)],
                ["Pickup", fmt(row.pickupDate)],
                ["ETA", fmt(row.estimatedDeliveryDate)],
                ["Road mode", row.roadTransportType],
                ["Truck type", row.truckType],
                ["Trucks", row.truckCount > 1 ? `${row.truckCount} · ${row.plates}` : row.plates],
                ["Service provider", row.serviceProvider],
                ["Weight / LDM", `${row.grossWeight.toLocaleString()} kg · ${row.ldm} LDM`],
            ],
            reasons: getPriorityReasons(row),
            milestones: steps.map((step, i) => ({ ...step, current: i === currentIndex })),
            checklists: [
                {
                    title: "Documents",
                    progress: true,
                    items: DOCS.map(([name, key]) => ({ name, ok: hasDoc(row[key]) })),
                },
            ],
            financials: { selling, buying, profit: selling - buying },
            openLabel: "Open RHFL file",
            onOpen: () => clickEditHandler(row.id),
        };
    };

    const csvColumns = [
        ["RHFL No", (r) => r.rhflNo],
        ["Customer", (r) => r.customer],
        ["Shipper", (r) => r.shipper],
        ["Consignee", (r) => r.consignee],
        ["Origin", (r) => place(r.pickupCity, r.pickupCountry)],
        ["Destination", (r) => place(r.deliveryCity, r.deliveryCountry)],
        ["Road mode", (r) => r.roadTransportType],
        ["Truck type", (r) => r.truckType],
        ["Trucks", (r) => r.truckCount],
        ["Plates", (r) => (r.plates === "—" ? "" : r.plates)],
        ["Service provider", (r) => r.serviceProvider],
        ["Pickup", (r) => csvDate(r.pickupDate)],
        ["ETA", (r) => csvDate(r.estimatedDeliveryDate)],
        ["Stage", (r) => getCurrentStage(r)],
        ["Priority", (r) => getPriority(r)],
        ["ADR", (r) => (r.adr ? "Yes" : "No")],
        ["Document health %", (r) => getDocumentHealth(r)],
        ["Missing documents", (r) => getMissingDocs(r).join(", ")],
        [`Selling (${currency})`, (r) => csvMoney(getConvertedSelling(r))],
        [`Buying (${currency})`, (r) => csvMoney(getConvertedBuying(r))],
        [`Profit (${currency})`, (r) => csvMoney(getConvertedProfit(r))],
    ];

    const selectedRow = rows.find((r) => r.id === selectedId);

    const linkSx = {
        color: UI.blue,
        cursor: "pointer",
        fontWeight: 600,
        fontSize: 13,
        "&:hover": { textDecoration: "underline" },
    };

    const fileControlTable = {
        columns: withRawSort([
            { Header: "RHFL No", accessor: "rhflNo" },
            { Header: "Customer", accessor: "customer" },
            { Header: "Origin / Destination", accessor: "route" },
            { Header: "ETA", accessor: "eta" },
            { Header: "Selling", accessor: "selling" },
            { Header: "Buying", accessor: "buying" },
            { Header: "Profit", accessor: "profit" },
            { Header: "Status", accessor: "status" },
        ]),
        rows: tableRows.map((row) => {
            const priority = getPriority(row);
            const selling = getConvertedSelling(row);
            const buying = getConvertedBuying(row);
            const profit = selling - buying;
            return {
                _sort: {
                    rhflNo: row.rhflNo,
                    customer: row.customer,
                    route: place(row.pickupCity, row.pickupCountry),
                    eta: dateValue(row.estimatedDeliveryDate),
                    selling,
                    buying,
                    profit,
                    status: PRIORITY_RANK[priority],
                },
                rhflNo: (
                    <MDTypography component="span" sx={linkSx} onClick={() => clickEditHandler(row.id)}>
                        {row.rhflNo}
                    </MDTypography>
                ),
                customer: <TruncatedText text={row.customer} max={40} />,
                route: (
                    <StackedCell
                        lines={[
                            ["Origin", place(row.pickupCity, row.pickupCountry)],
                            ["Destination", place(row.deliveryCity, row.deliveryCountry)],
                        ]}
                    />
                ),
                eta: row.estimatedDeliveryDate ? dayjs(row.estimatedDeliveryDate).format("DD/MM/YYYY") : "—",
                selling: formatMoney(selling, currency),
                buying: formatMoney(buying, currency),
                profit: (
                    <MDTypography
                        component="span"
                        sx={{ fontSize: 13, fontWeight: 700, color: profit > 0 ? UI.green : profit < 0 ? UI.red : UI.muted }}
                    >
                        {formatMoney(profit, currency)}
                    </MDTypography>
                ),
                status: (
                    <DetailsButton
                        priority={priority}
                        hint={`${getCurrentStage(row)} · ${priority} priority`}
                        onClick={() => setSelectedId(row.id)}
                    />
                ),
            };
        }),
    };

    if (loading) {
        return (
            <MDBox height="50vh" display="flex" alignItems="center" justifyContent="center">
                <CircularProgress />
            </MDBox>
        );
    }

    return (
        <MDBox display="flex" flexDirection="column" gap={2}>
            <DashboardToolbar
                search={search}
                onSearch={setSearch}
                placeholder="Search RHFL, customer, shipper, consignee, mode, provider, pickup, delivery..."
                currency={currency}
                onCurrency={setCurrency}
                filters={tableFilters}
                onRemoveFilter={removeFilter}
                onClear={clearFilter}
                dateRange={dateRange}
                onDateRange={setDateRange}
                dateLabel="Pickup"
                ratesMissing={exchangeRates === null}
                resultLabel={`${tableRows.length} files`}
            />

            <KpiGrid>
                <KpiCard
                    label="Open Files"
                    value={kpis.total}
                    icon={Truck}
                    tone="neutral"
                    trend={{ delta: monthDelta, label: "pickups this month vs last" }}
                    onClick={clearFilter}
                />
                <KpiCard
                    label="Action Required"
                    value={kpis.actionRequired}
                    hint="Loss, ADR, missing docs or near pickup/ETA"
                    icon={AlertTriangle}
                    tone="danger"
                    active={isFilterActive("actionRequired")}
                    onClick={() => setFilter("actionRequired")}
                />
                <KpiCard
                    label="Pickup ≤ 2 Days"
                    value={kpis.pickupSoon}
                    hint="By planned pickup date"
                    icon={PackageCheck}
                    tone="teal"
                    active={isFilterActive("pickupSoon")}
                    onClick={() => setFilter("pickupSoon")}
                />
                <KpiCard
                    label="Delivery ≤ 3 Days"
                    value={kpis.deliverySoon}
                    hint="By estimated delivery"
                    icon={MapPin}
                    tone="info"
                    active={isFilterActive("deliverySoon")}
                    onClick={() => setFilter("deliverySoon")}
                />
                <KpiCard
                    label="Document Health"
                    value={`${kpis.avgDocumentHealth}%`}
                    hint={`${kpis.missingDocuments} files with missing docs`}
                    icon={FileCheck2}
                    tone={kpis.avgDocumentHealth >= 80 ? "success" : "warning"}
                    active={isFilterActive("missingDocuments")}
                    onClick={() => setFilter("missingDocuments")}
                />
            </KpiGrid>

            <Grid container spacing={2}>
                <Grid item xs={12} lg={4}>
                    <Panel title="Shipment Stage" subtitle="Click a stage to filter the table">
                        <DonutWithLegend
                            data={stageRows}
                            totalLabel="files"
                            activeValue={activeFilterValue("stage")}
                            onSelect={(label) => setFilter("stage", label)}
                        />
                    </Panel>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Panel title="Action Center" subtitle="Items needing operational follow-up" bodySx={{ py: 1 }}>
                        <ActionList items={actionItems} />
                    </Panel>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Panel title="Financial Summary" subtitle={`Filtered files · ${currency}`}>
                        <MetricRow label="Selling" value={formatMoney(kpis.totalSelling, currency)} />
                        <MetricRow label="Buying" value={formatMoney(kpis.totalBuying, currency)} />
                        <MetricRow
                            label="Gross Profit"
                            value={formatMoney(kpis.totalProfit, currency)}
                            valueColor={kpis.totalProfit >= 0 ? UI.green : UI.red}
                            strong
                        />
                        <MetricRow
                            label="Margin"
                            value={`${kpis.margin.toFixed(1)}%`}
                            valueColor={kpis.margin >= 0 ? UI.text : UI.red}
                        />
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={7}>
                    <Panel
                        title="Upcoming Movements"
                        subtitle="Pickups and deliveries in the next 14 days · click for details"
                        bodySx={{ py: 1 }}
                    >
                        <TimelineList items={upcomingMovements} emptyText="No pickups or deliveries in the next 14 days" />
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={5}>
                    <Panel title="Operational Health" subtitle="Share of files in view">
                        <MDBox display="flex" flexDirection="column" gap={2}>
                            <ProgressStat label="Pickup planned" value={health.pickupPlanned} total={health.total} tone="teal" />
                            <ProgressStat
                                label="Documents complete"
                                value={health.docsComplete}
                                total={health.total}
                                tone={health.docsComplete / (health.total || 1) >= 0.8 ? "success" : "warning"}
                            />
                            <ProgressStat label="Departed" value={health.departed} total={health.total} tone="info" />
                            <ProgressStat label="Profitable" value={health.profitable} total={health.total} tone="success" />
                        </MDBox>
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={7}>
                    <Panel title="Breakdown" subtitle="Top 8 · click a row to filter the table">
                        <MDBox mb={2}>
                            <SegmentedTabs options={BREAKDOWNS} value={breakdown} onChange={setBreakdown} />
                        </MDBox>
                        <RankList
                            data={breakdownRows}
                            activeValue={activeFilterValue(breakdown)}
                            onSelect={(label) => setFilter(breakdown, label)}
                        />
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={5}>
                    <Panel title="Monthly Volume" subtitle="RHFL files by pickup month (last 12)">
                        <MDBox height={300}>
                            <Bar
                                data={{
                                    labels: monthlyRows.map((x) => dayjs(`${x.label}-01`).format("MMM YY")),
                                    datasets: [
                                        {
                                            label: "RHFL files",
                                            data: monthlyRows.map((x) => x.count),
                                            backgroundColor: UI.teal,
                                            borderRadius: 4,
                                            maxBarThickness: 28,
                                        },
                                    ],
                                }}
                                options={barChartOptions}
                            />
                        </MDBox>
                    </Panel>
                </Grid>
            </Grid>

            <TableCard
                cardRef={tableRef}
                onExport={() => downloadCsv("RHFL-files", csvColumns, tableRows)}
                title="RHFL File Control"
                summary={
                    tableFilters.length
                        ? `${tableRows.length} of ${filteredRows.length} files · ${tableFilters.map((f) => shortLabel(f.label, 40)).join(", ")}`
                        : `All files · ${tableRows.length}`
                }
            >
                <DataTable
                    table={fileControlTable}
                    entriesPerPage={{ defaultValue: 25, entries: [25, 50, 100] }}
                    showTotalEntries
                    isSorted
                    noEndBorder
                />
            </TableCard>

            <FileStatusDrawer
                status={selectedRow ? buildStatus(selectedRow) : null}
                currency={currency}
                onClose={() => setSelectedId(null)}
            />
        </MDBox>
    );
}

export default RhflDashboard;
